import type { GuideRailPart, RampPart, TableDefinition, TablePart, Vec2 } from './definition';
import { pathLength } from './flowMotion';

type Point = readonly [number, number];
interface FlowLayout {
  bumpers: readonly Point[];
  targets: readonly Point[];
  catch: Point;
  main: readonly Point[];
  side: readonly Point[];
  upper?: readonly Point[];
  upperFlippers?: readonly Point[];
  names: readonly [string, string, string, string, string];
}

// Authored shot fans, asymmetric pop fields, and staged upper-flipper feeds.
// Coordinates are normalized so the same canonical definition drives 2D, 3D,
// the editor and physics. See docs/TABLE_FLOW_REBUILD.md for the design audit.
const LAYOUTS: Record<string, FlowLayout> = {
  'grand-circuit-v1': {
    bumpers: [
      [0.34, 0.25],
      [0.55, 0.26],
      [0.46, 0.37],
    ],
    targets: [
      [0.26, 0.56],
      [0.45, 0.47],
      [0.69, 0.58],
    ],
    catch: [0.18, 0.17],
    main: [
      [0.71, 0.66],
      [0.77, 0.48],
      [0.68, 0.19],
      [0.42, 0.12],
      [0.2, 0.28],
      [0.25, 0.59],
      [0.41, 0.77],
    ],
    side: [
      [0.19, 0.64],
      [0.12, 0.45],
      [0.15, 0.19],
      [0.32, 0.08],
      [0.63, 0.08],
      [0.79, 0.23],
      [0.76, 0.54],
      [0.59, 0.77],
    ],
    names: ['Pit Stop', 'Grand Loop', 'Victory Lap', 'Pit Crew', 'Finish Line'],
  },
  'candy-carnival-v1': {
    bumpers: [
      [0.3, 0.25],
      [0.53, 0.23],
      [0.42, 0.35],
    ],
    targets: [
      [0.25, 0.5],
      [0.49, 0.45],
      [0.71, 0.49],
    ],
    catch: [0.21, 0.16],
    main: [
      [0.69, 0.66],
      [0.78, 0.48],
      [0.65, 0.39],
      [0.37, 0.4],
      [0.2, 0.51],
      [0.26, 0.65],
      [0.41, 0.77],
    ],
    side: [
      [0.19, 0.65],
      [0.13, 0.43],
      [0.18, 0.15],
      [0.4, 0.08],
      [0.68, 0.13],
      [0.8, 0.34],
      [0.74, 0.61],
      [0.59, 0.77],
    ],
    names: ['Prize Booth', 'Candy Slide', 'Sugar Spiral', 'Cotton Wheels', 'Sugar Jackpot'],
  },
  'cosmic-relay-v1': {
    bumpers: [
      [0.55, 0.25],
      [0.73, 0.32],
      [0.53, 0.39],
    ],
    targets: [
      [0.23, 0.56],
      [0.71, 0.52],
      [0.45, 0.46],
    ],
    catch: [0.22, 0.19],
    main: [
      [0.29, 0.67],
      [0.17, 0.48],
      [0.17, 0.24],
      [0.33, 0.1],
      [0.64, 0.1],
      [0.8, 0.23],
      [0.76, 0.54],
      [0.59, 0.77],
    ],
    side: [
      [0.73, 0.67],
      [0.79, 0.49],
      [0.72, 0.18],
      [0.44, 0.08],
      [0.15, 0.15],
      [0.13, 0.43],
      [0.24, 0.64],
      [0.41, 0.77],
    ],
    names: ['Relay Uplink', 'Slingshot Orbit', 'Comet Return', 'Meteor Field', 'Relay Network'],
  },
  'neon-rush-v1': {
    bumpers: [
      [0.36, 0.21],
      [0.54, 0.32],
      [0.65, 0.43],
    ],
    targets: [
      [0.23, 0.48],
      [0.4, 0.52],
      [0.67, 0.57],
    ],
    catch: [0.2, 0.17],
    main: [
      [0.75, 0.66],
      [0.78, 0.5],
      [0.75, 0.26],
      [0.59, 0.11],
      [0.3, 0.12],
      [0.17, 0.36],
      [0.23, 0.63],
      [0.41, 0.77],
    ],
    side: [
      [0.22, 0.65],
      [0.14, 0.45],
      [0.2, 0.19],
      [0.41, 0.08],
      [0.71, 0.13],
      [0.81, 0.38],
      [0.71, 0.65],
      [0.59, 0.77],
    ],
    names: ['Ignition', 'Overdrive', 'Express Loop', 'Reactor Cells', 'Breaker Chain'],
  },
  'haunted-keep-v1': {
    bumpers: [
      [0.26, 0.24],
      [0.49, 0.21],
      [0.38, 0.35],
    ],
    targets: [
      [0.3, 0.44],
      [0.53, 0.45],
      [0.71, 0.42],
    ],
    catch: [0.18, 0.15],
    main: [
      [0.74, 0.68],
      [0.8, 0.48],
      [0.77, 0.22],
      [0.54, 0.12],
      [0.44, 0.26],
      [0.65, 0.51],
    ],
    side: [
      [0.2, 0.68],
      [0.12, 0.43],
      [0.17, 0.13],
      [0.42, 0.08],
      [0.7, 0.12],
      [0.81, 0.35],
      [0.76, 0.62],
      [0.59, 0.77],
    ],
    upper: [
      [0.21, 0.49],
      [0.14, 0.34],
      [0.27, 0.17],
      [0.5, 0.12],
      [0.7, 0.25],
      [0.75, 0.56],
      [0.41, 0.77],
    ],
    upperFlippers: [
      [0.23, 0.61],
      [0.73, 0.58],
    ],
    names: ['Séance', 'Portcullis', 'Graveyard Loop', 'Dead Bells', 'Keep Guardians'],
  },
  'jungle-temple-v1': {
    bumpers: [
      [0.49, 0.25],
      [0.72, 0.21],
      [0.62, 0.36],
    ],
    targets: [
      [0.23, 0.46],
      [0.45, 0.52],
      [0.69, 0.49],
    ],
    catch: [0.19, 0.21],
    main: [
      [0.29, 0.67],
      [0.18, 0.49],
      [0.22, 0.25],
      [0.39, 0.16],
      [0.44, 0.38],
      [0.65, 0.49],
      [0.7, 0.64],
      [0.59, 0.77],
    ],
    side: [
      [0.2, 0.63],
      [0.12, 0.43],
      [0.17, 0.15],
      [0.41, 0.08],
      [0.7, 0.12],
      [0.81, 0.34],
      [0.75, 0.61],
      [0.59, 0.77],
    ],
    names: ['Relic Chamber', 'Waterfall', 'Canopy Run', 'Relic Idols', 'Temple Guardians'],
  },
  'deep-sea-dive-v1': {
    bumpers: [
      [0.29, 0.28],
      [0.56, 0.24],
      [0.67, 0.37],
    ],
    targets: [
      [0.27, 0.5],
      [0.46, 0.52],
      [0.69, 0.52],
    ],
    catch: [0.18, 0.19],
    main: [
      [0.72, 0.68],
      [0.79, 0.53],
      [0.75, 0.4],
      [0.49, 0.44],
      [0.34, 0.64],
      [0.41, 0.77],
    ],
    side: [
      [0.21, 0.65],
      [0.12, 0.43],
      [0.16, 0.16],
      [0.39, 0.08],
      [0.69, 0.13],
      [0.8, 0.36],
      [0.75, 0.6],
      [0.59, 0.77],
    ],
    names: ['Pressure Lock', 'Trench Dive', 'Surface Current', 'Reef Currents', 'Trench Beacon'],
  },
  'clockwork-district-v1': {
    bumpers: [
      [0.32, 0.25],
      [0.58, 0.29],
      [0.45, 0.41],
    ],
    targets: [
      [0.23, 0.5],
      [0.5, 0.53],
      [0.71, 0.48],
    ],
    catch: [0.17, 0.18],
    main: [
      [0.73, 0.69],
      [0.79, 0.49],
      [0.7, 0.21],
      [0.44, 0.13],
      [0.23, 0.24],
      [0.31, 0.48],
      [0.59, 0.77],
    ],
    side: [
      [0.18, 0.64],
      [0.12, 0.42],
      [0.19, 0.17],
      [0.44, 0.08],
      [0.71, 0.16],
      [0.8, 0.41],
      [0.73, 0.63],
      [0.59, 0.77],
    ],
    upper: [
      [0.29, 0.65],
      [0.22, 0.44],
      [0.33, 0.22],
      [0.58, 0.17],
      [0.75, 0.37],
      [0.73, 0.61],
      [0.59, 0.77],
    ],
    names: ['Winding Key', 'Mainspring', 'Clockwise Loop', 'Governor', 'Automaton'],
  },
  'frontier-run-v1': {
    bumpers: [
      [0.31, 0.27],
      [0.53, 0.23],
      [0.38, 0.38],
    ],
    targets: [
      [0.24, 0.52],
      [0.47, 0.49],
      [0.7, 0.54],
    ],
    catch: [0.2, 0.16],
    main: [
      [0.7, 0.67],
      [0.77, 0.48],
      [0.63, 0.35],
      [0.28, 0.32],
      [0.17, 0.43],
      [0.25, 0.61],
      [0.41, 0.77],
    ],
    side: [
      [0.18, 0.64],
      [0.12, 0.4],
      [0.19, 0.15],
      [0.4, 0.08],
      [0.7, 0.14],
      [0.81, 0.39],
      [0.73, 0.65],
      [0.59, 0.77],
    ],
    upper: [
      [0.24, 0.64],
      [0.18, 0.45],
      [0.26, 0.27],
      [0.49, 0.21],
      [0.71, 0.38],
      [0.68, 0.61],
      [0.41, 0.77],
    ],
    names: ['Rail Depot', 'Railway Chase', 'Canyon Express', 'Posse', 'Wanted Outlaws'],
  },
  'dragon-keep-v1': {
    bumpers: [
      [0.51, 0.18],
      [0.72, 0.29],
      [0.49, 0.34],
    ],
    targets: [
      [0.28, 0.4],
      [0.5, 0.45],
      [0.71, 0.43],
    ],
    catch: [0.16, 0.23],
    main: [
      [0.74, 0.69],
      [0.8, 0.48],
      [0.76, 0.2],
      [0.56, 0.11],
      [0.35, 0.28],
      [0.32, 0.56],
    ],
    side: [
      [0.19, 0.69],
      [0.12, 0.42],
      [0.18, 0.14],
      [0.43, 0.08],
      [0.7, 0.13],
      [0.81, 0.35],
      [0.76, 0.62],
      [0.59, 0.77],
    ],
    upper: [
      [0.23, 0.49],
      [0.16, 0.31],
      [0.3, 0.13],
      [0.57, 0.12],
      [0.76, 0.32],
      [0.7, 0.62],
      [0.59, 0.77],
    ],
    upperFlippers: [
      [0.25, 0.64],
      [0.74, 0.58],
    ],
    names: ['Dragon Fury', 'Dragon Flight', 'Lava Circuit', 'Fire Vents', 'Boss Gauntlet'],
  },
};

const LOWER_HARDWARE = new Set([
  'flip-l',
  'flip-r',
  'sling-left',
  'sling-right',
  'drain',
  'plunger-lane',
  'guide-left-outlane',
  'guide-right-outlane',
]);

const TARGET_NAMES: Record<string, readonly string[]> = {
  'grand-circuit-v1': ['Sector 1', 'Sector 2', 'Final Sector'],
  'candy-carnival-v1': ['Left Prize', 'Sugar Jackpot', 'Right Prize'],
  'cosmic-relay-v1': ['Alpha', 'Beta', 'Gamma'],
  'neon-rush-v1': ['Breaker I', 'Breaker II', 'Breaker III'],
  'haunted-keep-v1': ['Stone Gate', 'Ghost', 'Gargoyle'],
  'jungle-temple-v1': ['Stone Guardian', 'Heart Crystal', 'Sky Guardian'],
  'deep-sea-dive-v1': ['Pressure Lock', 'Trench Beacon', 'Coral Guardian'],
  'clockwork-district-v1': ['Escapement I', 'Automaton', 'Release Valve'],
  'frontier-run-v1': ['Scout', 'Gunfighter', 'Outlaw Boss'],
  'dragon-keep-v1': ['Keep Gate', 'Fire Crystal', 'Dragon Guardian'],
};

export function rebuildTableFlow(table: TableDefinition): TableDefinition {
  const layout = LAYOUTS[table.id];
  if (!layout) return table;
  const { width: w, height: h } = table.board;
  const point = ([x, y]: Point): Vec2 => ({ x: x * w, y: y * h });
  const palette = table.theme;
  // Replace the accumulated orphan rails/funnels, rather than layering another
  // patch onto them. Preserve mechanism IDs, staged targets, awards and artwork.
  table.parts = table.parts.filter(
    (p) => LOWER_HARDWARE.has(p.id) || !['wall', 'guide-rail', 'post', 'insert'].includes(p.kind),
  );
  const m = Math.max(1.6, w * 0.035);
  const top = m;
  const shoulder = top + w * 0.055;
  const shell: TablePart[] = [
    {
      id: 'wall-left',
      kind: 'wall',
      scoreValue: 0,
      thickness: 1.1,
      points: [
        { x: m, y: shoulder },
        { x: m, y: h * 0.995 },
      ],
    },
    {
      id: 'wall-top',
      kind: 'wall',
      scoreValue: 0,
      thickness: 1.1,
      points: [
        { x: m, y: shoulder },
        { x: m + w * 0.055, y: top },
        { x: w - m - w * 0.055, y: top },
        { x: w - m, y: shoulder },
      ],
    },
    {
      id: 'wall-right',
      kind: 'wall',
      scoreValue: 0,
      thickness: 1.1,
      points: [
        { x: w - m, y: shoulder },
        { x: w - m, y: h * 0.995 },
      ],
    },
    {
      id: 'apron-left',
      kind: 'wall',
      scoreValue: 0,
      thickness: 1.1,
      points: [{ x: m, y: h * 0.945 }, point([0.15, 0.973]), point([0.37, 0.995])],
    },
    {
      id: 'apron-right',
      kind: 'wall',
      scoreValue: 0,
      thickness: 1.1,
      points: [{ x: w - m, y: h * 0.945 }, point([0.85, 0.973]), point([0.63, 0.995])],
    },
  ];
  table.parts.unshift(...shell);

  // One continuous inlane/outlane divider joins each pivot. The former inner
  // rail ran through a sling; its cap and nearby post made a ball-sized pocket.
  for (const side of ['left', 'right'] as const) {
    const rail = table.parts.find((p) => p.id === `guide-${side}-outlane`);
    if (rail?.kind !== 'guide-rail') continue;
    const path: Point[] = [
      [0.14, 0.7],
      [0.14, 0.84],
      [0.23, 0.89],
      [17 / 52, 95 / 104],
    ];
    rail.points = path.map(([x, y]) => point([side === 'left' ? x : 1 - x, y]));
    rail.thickness = 0.7;
  }

  const ids = ['bumper-left', 'bumper-right', 'bumper-center'];
  ids.forEach((id, i) => {
    const part = table.parts.find((p) => p.id === id);
    if (part?.kind === 'bumper') {
      part.center = point(layout.bumpers[i]!);
      part.scatterDeg =
        table.id === 'neon-rush-v1' ? 4 : table.id === 'deep-sea-dive-v1' ? 1.5 : 2.5;
      part.label = layout.names[3];
    }
  });
  ['target-1', 'target-2', 'target-3'].forEach((id, i) => {
    const part = table.parts.find((p) => p.id === id);
    if (part?.kind === 'target') {
      part.center = point(layout.targets[i]!);
      part.label = TARGET_NAMES[table.id]?.[i] ?? layout.names[4];
    }
  });
  for (const part of table.parts) {
    if (part.kind === 'kicker') {
      part.center = point(layout.catch);
      part.label = layout.names[0];
    }
    if (part.kind === 'rollover' && ['roll-a', 'roll-b', 'roll-c'].includes(part.id)) {
      const i = ['roll-a', 'roll-b', 'roll-c'].indexOf(part.id);
      part.center = point([0.32 + i * 0.17, 0.125]);
    }
    if (part.kind === 'plunger-lane') {
      part.label = 'Skill Plunge';
      part.skillShotSpread = 0.34;
      // A straight divider and one rounded roof return, with no diagonal
      // shelves or opposed rails next to the shooter lane.
      if (part.divider)
        part.divider.points = [
          { x: part.bounds.x, y: h * 0.2 },
          { x: part.bounds.x, y: h * 0.965 },
        ];
      const rest = part.restPosition!;
      part.launchPath = [
        rest,
        { x: rest.x, y: h * 0.25 },
        { x: rest.x, y: shoulder + 3 },
        point([0.84, 0.09]),
        point([0.66, 0.13]),
      ];
    }
    if (
      (part.kind === 'flipper-left' || part.kind === 'flipper-right') &&
      part.id.startsWith('flip-upper-')
    ) {
      const i = part.kind === 'flipper-left' ? 0 : 1;
      if (layout.upperFlippers) {
        part.pivot = point(layout.upperFlippers[i]!);
        part.length = w * 0.11;
      }
    }
  }

  const route = (
    id: string,
    path: readonly Point[],
    label: string,
    score: number,
    isOrbit = false,
  ): void => {
    let part = table.parts.find((p): p is RampPart => p.id === id && p.kind === 'ramp');
    if (!part) {
      part = {
        id,
        kind: 'ramp',
        scoreValue: score,
        entry: point(path[0]!),
        path: [],
        exit: point(path.at(-1)!),
        exitVelocity: { x: 0, y: 0 },
        style: 'wireform',
        width: 4.6,
      };
      table.parts.push(part);
    }
    part.path = path.map(point);
    part.entry = { ...part.path[0]! };
    part.exit = { ...part.path.at(-1)! };
    part.exitVelocity = {
      x: part.exit.x < w * 0.5 ? -7 : 7,
      y: table.id === 'neon-rush-v1' ? 27 : 18,
    };
    if (part.exit.y < h * 0.65) part.exitVelocity = { x: part.exit.x < w * 0.5 ? 4 : -4, y: 18 };
    part.label = label;
    part.transport = isOrbit ? 'orbit' : 'ramp';
    const speed = table.id === 'neon-rush-v1' ? 225 : table.id === 'deep-sea-dive-v1' ? 135 : 175;
    part.travelSeconds = Math.max(0.65, Math.min(1.7, pathLength(part.path) / speed));
    if (isOrbit) {
      part.width = 3.8;
      part.color = palette.secondaryColor ?? palette.accentColor;
    }
  };
  route('ramp-main', layout.main, layout.names[1], 1500);
  if (layout.upper)
    route(
      'ramp-upper-loop',
      layout.upper,
      table.id === 'clockwork-district-v1'
        ? 'Escapement'
        : table.id === 'frontier-run-v1'
          ? 'Ore Train'
          : 'Upper Escape',
      2200,
    );
  route('flow-orbit', layout.side, layout.names[2], 900, true);

  for (const ramp of table.parts.filter((p): p is RampPart => p.kind === 'ramp')) {
    const next = ramp.path[1]!;
    const dx = next.x - ramp.entry.x;
    const dy = next.y - ramp.entry.y;
    const length = Math.hypot(dx, dy) || 1;
    table.parts.push({
      id: `insert-${ramp.id}`,
      kind: 'insert',
      scoreValue: 0,
      center: { x: ramp.entry.x - (dx / length) * 4.5, y: ramp.entry.y - (dy / length) * 4.5 },
      shape: 'arrow',
      width: 2,
      height: 3.2,
      rotationDeg: (Math.atan2(dy, dx) * 180) / Math.PI + 90,
      color: ramp.color ?? palette.accentColor,
      emissive: 0.85,
    });
  }

  // Roof posts introduce rubber rebounds without enclosing the bumper field.
  const roof: GuideRailPart = {
    id: 'shooter-return-guide',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 0.7,
    points: [point([0.88, 0.19]), point([0.86, 0.1]), point([0.79, 0.075]), point([0.72, 0.095])],
    color: palette.metalColor,
  };
  // The shooter return is already carried by its path; a physical inside rail
  // here would close a wedge against the divider. Render the canopy only.
  roof.collider = false;
  table.parts.push(roof);
  // Keep instructions attached to the rebuilt shots rather than retired names.
  for (const mission of table.rules?.missions ?? []) {
    const kind = mission.trigger === 'prop-destroy' ? 'target' : mission.trigger;
    const labels = [
      ...new Set(
        table.parts
          .filter(
            (part) =>
              part.kind === kind && (!mission.partIds?.length || mission.partIds.includes(part.id)),
          )
          .map((part) => part.label)
          .filter(Boolean),
      ),
    ];
    const named = labels.length > 0 && labels.length <= 2 ? labels.join(' / ') : undefined;
    mission.description =
      mission.trigger === 'kicker'
        ? `Catch the ball at ${named ?? 'the lit cup'}.`
        : mission.trigger === 'ramp'
          ? `Shoot ${named ?? 'the lit routes'}.`
          : mission.trigger === 'prop-destroy'
            ? 'Break the lit targets.'
            : mission.trigger === 'bumper'
              ? 'Work the lit pop bumpers.'
              : mission.trigger === 'rollover'
                ? 'Roll through the lit lanes.'
                : 'Hit the lit targets.';
  }
  return table;
}
